import { useEffect, useMemo, useState } from "react";
import { Trophy, Loader2, X, Lock } from "lucide-react";
import { cn } from "../lib/utils";
import { getImageUrl } from "../lib/coachUtils";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";

// "production_optics" → "Production optics": la division quando il
// riferimento non ha un nome leggibile da dare.
const humanizeDivision = (value) => {
	const text = String(value || "")
		.trim()
		.replace(/_/g, " ");
	return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

/** La division di una riga di tiro, con il power factor se c'è. */
const shootingDivisionLabel = (entry) => {
	const shooting = entry?.shooting;
	if (!shooting?.division) return "";
	const division =
		shooting.division_label || humanizeDivision(shooting.division);
	const power =
		shooting.power_factor === "major"
			? "Major"
			: shooting.power_factor === "minor"
				? "Minor"
				: "";
	return power ? `${division} · ${power}` : division;
};

export function LeaderboardDialog({
	open,
	onOpenChange,
	title,
	description,
	loading,
	error,
	data,
	genderFilter,
	onGenderFilterChange,
	scopeOptions,
	scopeValue,
	onScopeChange,
	formatMetric,
	getEntryMetaLabel,
	t,
	emptyTitle,
	emptyDescription,
	contentClassName,
	schemaFields,
	activeSchemaFieldId,
	onActiveSchemaFieldChange,
	showScalingBadges,
	showPrivacyBadges,
	// Filtri in piu' del chiamante, sotto quelli di genere: la classifica del
	// profilo prestativo ci mette categoria di peso e ordinamento per
	// coefficiente (D14 di docs/sport-config). Chi non li passa non cambia.
	extraFilters = null,
	// Nascondere i tab di genere quando un filtro del chiamante li rende
	// superflui (una categoria di peso e' gia' di un genere).
	hideGenderFilter = false,
}) {
	const allEntries = useMemo(
		() => (Array.isArray(data?.entries) ? data.entries : []),
		[data?.entries],
	);
	// Tiro: la division dell'arma, come RX/SCALED nel conditioning. Prove in
	// division diverse non si leggono alla pari, quindi con più di una
	// division si può guardare la classifica di ciascuna. Le righe che non
	// sono di tiro non hanno `shooting`, e per loro non cambia niente.
	const [divisionFilter, setDivisionFilter] = useState("");
	const divisionOptions = useMemo(() => {
		const seen = new Map();
		allEntries.forEach((entry) => {
			const value = entry?.shooting?.division;
			if (value && !seen.has(value)) {
				seen.set(value, {
					value,
					label: entry.shooting.division_label || humanizeDivision(value),
				});
			}
		});
		return [...seen.values()];
	}, [allEntries]);
	const entries =
		divisionFilter && divisionOptions.length > 1
			? allEntries.filter(
					(entry) => entry?.shooting?.division === divisionFilter,
				)
			: allEntries;
	const isDivisionView = entries !== allEntries;
	const schemaTabs = Array.isArray(schemaFields) ? schemaFields : [];
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewAthlete, setPreviewAthlete] = useState(null);
	const [avatarCandidateIndexById, setAvatarCandidateIndexById] = useState({});
	const [supersetActiveIndex, setSupersetActiveIndex] = useState(0);

	const getInitials = (name) => {
		const cleaned = String(name || "").trim();
		if (!cleaned) return "AT";
		const parts = cleaned.split(/\s+/).filter(Boolean);
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
	};

	const genderTabs = [
		{ value: "all", label: t("leaderboardGenderAll") },
		{ value: "male", label: t("genderMale") },
		{ value: "female", label: t("genderFemale") },
	];

	const supersetTabs = useMemo(() => {
		const entriesWithSuperset = entries.filter(
			(entry) =>
				Array.isArray(entry?.superset_details) &&
				entry.superset_details.length > 1,
		);
		if (entriesWithSuperset.length === 0) return [];

		const maxDetailCount = Math.max(
			0,
			...entriesWithSuperset.map((entry) => entry.superset_details.length),
		);
		if (maxDetailCount <= 1) return [];

		return Array.from({ length: maxDetailCount }, (_, idx) => {
			const fallbackName = entriesWithSuperset
				.map((entry) => entry?.superset_details?.[idx]?.exercise_name)
				.find((name) => typeof name === "string" && name.trim().length > 0);
			const letterLabel = String.fromCharCode(65 + idx);
			return {
				index: idx,
				label: fallbackName || letterLabel,
				exerciseName: fallbackName || "",
			};
		});
	}, [entries]);

	useEffect(() => {
		if (!open) setDivisionFilter("");
	}, [open]);

	useEffect(() => {
		if (!open) {
			setSupersetActiveIndex(0);
			return;
		}
		if (supersetTabs.length === 0) {
			setSupersetActiveIndex(0);
			return;
		}
		if (supersetActiveIndex >= supersetTabs.length) {
			setSupersetActiveIndex(0);
		}
	}, [open, supersetTabs, supersetActiveIndex]);

	const getAvatarCandidates = (entry) => {
		const rawCandidates = [entry?.athlete_picture]
			.filter((value) => typeof value === "string")
			.map((value) => value.trim())
			.filter(Boolean);

		const expanded = [];
		rawCandidates.forEach((raw) => {
			expanded.push(raw);

			if (/^av_[a-z0-9]+$/i.test(raw)) {
				expanded.push(`/api/users/picture/${raw}`);
			}

			if (raw.startsWith("api/")) {
				expanded.push(`/${raw}`);
			}

			if (raw.startsWith("users/picture/")) {
				expanded.push(`/api/${raw}`);
			}

			if (
				!raw.startsWith("/") &&
				!raw.startsWith("http://") &&
				!raw.startsWith("https://") &&
				!raw.startsWith("data:") &&
				!raw.startsWith("blob:")
			) {
				expanded.push(`/${raw}`);
			}
		});

		const normalized = expanded
			.map((candidate) => getImageUrl(candidate))
			.filter(Boolean);

		return Array.from(new Set(normalized));
	};

	const openPreview = (entry) => {
		const athleteKey = entry?.athlete_id || entry?.athlete_name || "unknown";
		const candidates = getAvatarCandidates(entry);
		const candidateIndex = avatarCandidateIndexById[athleteKey] || 0;
		const imageUrl = candidates[candidateIndex] || candidates[0] || null;
		if (!imageUrl) return;
		setPreviewAthlete({
			name: entry?.athlete_name || "Athlete",
			imageUrl,
		});
		setPreviewOpen(true);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={cn(
					"w-[calc(100vw-1rem)] sm:w-full max-w-lg bg-card border-border rounded-sm p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden dialog-mobile-safe",
					contentClassName,
				)}
			>
				<DialogDescription className="hidden" />
				<DialogHeader className="shrink-0 px-4 py-3 border-b border-border">
					<DialogTitle className="font-heading text-base flex items-center gap-2">
						<Trophy className="w-4 h-4 text-yellow-500" />
						{title}
					</DialogTitle>
					{description ? (
						<p className="text-xs text-muted-foreground pr-6">{description}</p>
					) : null}
				</DialogHeader>

				<div className="shrink-0 px-4 pt-4 pb-3 space-y-3 border-b border-border">
					{scopeOptions?.length > 1 && (
						<div className="flex items-center gap-1 overflow-x-auto pb-1 no-scrollbar">
							{scopeOptions.map((option) => (
								<button
									key={option.value || "sport_scope"}
									type="button"
									onClick={() => onScopeChange?.(option.value)}
									className={cn(
										"h-8 px-2.5 rounded-sm border text-xs font-heading whitespace-nowrap",
										scopeValue === option.value
											? "border-volt/60 bg-volt/15 text-volt"
											: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
									)}
								>
									{option.label}
								</button>
							))}
						</div>
					)}
					{schemaTabs.length > 0 && (
						<div className="flex flex-wrap items-center gap-1">
							{schemaTabs.map((field) => (
								<button
									key={field.field_id}
									type="button"
									onClick={() =>
										onActiveSchemaFieldChange &&
										onActiveSchemaFieldChange(field.field_id)
									}
									className={cn(
										"h-8 px-2.5 rounded-sm border text-xs font-heading transition-colors",
										activeSchemaFieldId === field.field_id
											? "border-volt/60 bg-volt/15 text-volt"
											: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
									)}
								>
									{field.title || "Risultato"}
								</button>
							))}
						</div>
					)}
					{!hideGenderFilter && (
						<div className="flex flex-wrap items-center gap-1">
							{genderTabs.map((tab) => (
								<button
									key={tab.value}
									type="button"
									onClick={() => onGenderFilterChange(tab.value)}
									className={cn(
										"h-8 px-2.5 rounded-sm border text-xs font-heading transition-colors",
										genderFilter === tab.value
											? "border-volt/60 bg-volt/15 text-volt"
											: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
									)}
								>
									{tab.label}
								</button>
							))}
						</div>
					)}
					{extraFilters}

					{divisionOptions.length > 1 && (
						<div className="flex items-center gap-1 overflow-x-auto pb-1 no-scrollbar">
							{[
								{ value: "", label: t("leaderboardGenderAll") },
								...divisionOptions,
							].map((option) => (
								<button
									key={option.value || "all_divisions"}
									type="button"
									onClick={() => setDivisionFilter(option.value)}
									className={cn(
										"h-8 px-2.5 rounded-sm border text-xs font-heading whitespace-nowrap shrink-0 transition-colors",
										divisionFilter === option.value
											? "border-sky-400/60 bg-sky-500/15 text-sky-300"
											: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
									)}
								>
									{option.label}
								</button>
							))}
						</div>
					)}

					{supersetTabs.length > 1 && (
						<div className="space-y-1">
							<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
								{t("exerciseLabel")}
							</p>
							<div className="flex items-center gap-1 overflow-x-auto pb-1 no-scrollbar">
								{supersetTabs.map((tab) => (
									<button
										key={`superset_tab_${tab.index}`}
										type="button"
										onClick={() => setSupersetActiveIndex(tab.index)}
										className={cn(
											"h-8 px-2.5 rounded-sm border text-xs font-heading transition-colors whitespace-nowrap shrink-0",
											supersetActiveIndex === tab.index
												? "border-volt/60 bg-volt/15 text-volt"
												: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
										)}
										title={tab.label || undefined}
									>
										{tab.label}
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				<div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
					{loading ? (
						<div className="flex items-center justify-center py-8 text-muted-foreground">
							<Loader2 className="w-5 h-5 animate-spin" />
						</div>
					) : error ? (
						<div className="rounded-sm border border-blaze/30 bg-blaze/10 px-3 py-2 text-xs text-blaze">
							{error}
						</div>
					) : entries.length ? (
						entries.map((entry, entryIndex) => {
							const divisionLabel = shootingDivisionLabel(entry);
							const athleteKey =
								entry.athlete_id ||
								entry.athlete_name ||
								String(entry.rank || "");
							const meta = getEntryMetaLabel ? getEntryMetaLabel(entry) : "";
							const metricEntry =
								supersetTabs.length > 1
									? {
											...entry,
											__superset_active_index: supersetActiveIndex,
										}
									: entry;
							const metric = formatMetric
								? formatMetric(metricEntry)
								: entry.metric_label || entry.value || "-";
							// Se il metric è testo libero lungo (score senza tipo configurato),
							// lo mostriamo nell'area contenuto e non nella colonna destra
							const metricStr =
								typeof metric === "string" ? metric : String(metric || "");
							const isLongFreeText =
								metricStr.length > 15 &&
								metricStr.includes(" ") &&
								!/^\d+[:'\-]\d{2}(\.\d+)?$/.test(metricStr.trim()) &&
								!/^\d+(\.\d+)?\s*(kg|lb|m|km|cal|rep|reps)?$/i.test(
									metricStr.trim(),
								);
							const scalingText =
								typeof entry.scaling === "string" ? entry.scaling.trim() : "";
							const hasScaledValue =
								scalingText !== "" && scalingText.toLowerCase() !== "rx";
							const avatarCandidates = getAvatarCandidates(entry);
							const avatarCandidateIndex =
								avatarCandidateIndexById[athleteKey] || 0;
							const athleteAvatarUrl =
								avatarCandidates[avatarCandidateIndex] ||
								avatarCandidates[0] ||
								null;
							const canPreviewAvatar = Boolean(athleteAvatarUrl);
							return (
								<div
									key={athleteKey}
									className={cn(
										"rounded-sm border px-3 py-2.5 flex items-start gap-2.5",
										entry.is_current_user
											? "border-volt/40 bg-volt/10"
											: "border-border bg-background/60",
									)}
								>
									<div className="w-7 shrink-0 pt-1 text-center font-heading text-xs text-muted-foreground">
										#{isDivisionView ? entryIndex + 1 : entry.rank}
									</div>
									<button
										type="button"
										onClick={() => openPreview(entry)}
										disabled={!canPreviewAvatar}
										className={cn(
											"shrink-0 rounded-sm",
											canPreviewAvatar
												? "cursor-zoom-in hover:opacity-90"
												: "cursor-default",
										)}
										aria-label={
											canPreviewAvatar
												? `Apri anteprima foto ${entry.athlete_name || "atleta"}`
												: `Foto profilo ${entry.athlete_name || "atleta"}`
										}
									>
										<Avatar className="w-9 h-9 shrink-0 border border-border">
											<AvatarImage
												src={athleteAvatarUrl || undefined}
												onError={() => {
													if (
														avatarCandidateIndex <
														avatarCandidates.length - 1
													) {
														setAvatarCandidateIndexById((prev) => ({
															...prev,
															[athleteKey]: avatarCandidateIndex + 1,
														}));
													}
												}}
											/>
											<AvatarFallback className="bg-volt/20 text-volt text-[11px] font-heading">
												{getInitials(entry.athlete_name)}
											</AvatarFallback>
										</Avatar>
									</button>
									<div className="flex-1 min-w-0 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-1.5 min-w-0">
												<p className="text-sm font-medium truncate">
													{entry.athlete_name}
												</p>
												{entry.athlete_gender ? (
													<span
														className={cn(
															"text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5",
															entry.athlete_gender === "F"
																? "bg-rose-500/20 text-rose-300"
																: "bg-cyan-500/20 text-cyan-300",
														)}
													>
														{entry.athlete_gender}
													</span>
												) : null}
												{divisionLabel ? (
													<span className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-sky-500/15 text-sky-300 border border-sky-500/30">
														{divisionLabel}
													</span>
												) : null}
												{entry.weight_class?.label ? (
													<span className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-secondary text-foreground/80 border border-border">
														{entry.weight_class.label}
													</span>
												) : null}
												{entry.athlete_bodyweight ? (
													<span className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-secondary text-muted-foreground">
														{t("bodyweightShort")}{" "}
														{Number(entry.athlete_bodyweight) % 1 === 0
															? Number(entry.athlete_bodyweight)
															: Number(entry.athlete_bodyweight).toFixed(1)}
														kg
													</span>
												) : null}
												{entry.is_current_user ? (
													<span className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-volt/20 text-volt">
														{t("youLabel")}
													</span>
												) : null}
												{showPrivacyBadges && entry.is_public === false ? (
													<span
														className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-secondary text-muted-foreground border border-border inline-flex items-center gap-1"
														title={t("leaderboardPrivateResultHint")}
													>
														<Lock className="w-2.5 h-2.5" />
														{t("leaderboardPrivateLabel")}
													</span>
												) : null}
												{hasScaledValue ? (
													<span
														className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30"
														title={scalingText}
													>
														{(t && t("scalingScaledLabel")) || "SCALED"}
													</span>
												) : entry.rank_mode && showScalingBadges ? (
													<span className="text-[10px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-green-500/20 text-green-300 border border-green-500/30">
														{(t && t("scalingRxLabel")) || "RX"}
													</span>
												) : null}
											</div>
											{isLongFreeText ? (
												<p
													className="text-[11px] text-foreground/80 leading-snug line-clamp-3 mt-0.5"
													title={metricStr}
												>
													{metricStr}
												</p>
											) : null}
											{meta ? (
												<p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
													{meta}
												</p>
											) : null}
											{hasScaledValue ? (
												<p className="text-[11px] text-amber-300/80 leading-snug line-clamp-2">
													{scalingText}
												</p>
											) : null}
										</div>
										<div className="w-full sm:w-auto sm:shrink-0 text-right sm:pl-1 sm:min-w-[52px] sm:max-w-[120px] self-end sm:self-start">
											<p className="font-heading text-base leading-tight text-foreground break-words sm:break-all line-clamp-3">
												{isLongFreeText ? "-" : metric}
											</p>
										</div>
									</div>
								</div>
							);
						})
					) : (
						<div className="rounded-sm border border-border bg-background/40 px-4 py-6 text-center">
							<p className="font-heading text-sm mb-1">
								{emptyTitle || t("noResults")}
							</p>
							<p className="text-xs text-muted-foreground">
								{emptyDescription || t("leaderboardEmptyLabel")}
							</p>
						</div>
					)}
				</div>
			</DialogContent>
			<Dialog
				open={previewOpen}
				onOpenChange={(nextOpen) => {
					setPreviewOpen(nextOpen);
					if (!nextOpen) setPreviewAthlete(null);
				}}
			>
				<DialogContent className="max-w-md p-0 border-border bg-card rounded-sm overflow-hidden">
					<DialogDescription className="hidden" />
					<div className="relative">
						<button
							type="button"
							onClick={() => {
								setPreviewOpen(false);
								setPreviewAthlete(null);
							}}
							className="absolute top-2 right-2 z-10 h-8 w-8 rounded-sm bg-background/70 border border-border text-foreground hover:bg-background flex items-center justify-center"
							aria-label="Chiudi anteprima"
						>
							<X className="w-4 h-4" />
						</button>
						{previewAthlete?.imageUrl ? (
							<img
								src={previewAthlete.imageUrl}
								alt={previewAthlete.name || "Athlete"}
								className="w-full h-auto max-h-[70vh] object-cover"
							/>
						) : null}
					</div>
					{previewAthlete?.name ? (
						<div className="px-3 py-2 border-t border-border bg-background/60">
							<p className="text-sm font-medium truncate">
								{previewAthlete.name}
							</p>
						</div>
					) : null}
				</DialogContent>
			</Dialog>
		</Dialog>
	);
}
